import { AnchorLink } from 'gatsby-plugin-anchor-links';
import React from 'react';
import * as slugs from 'github-slugger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHorizontalRule,
  faDash,
  faHyphen,
} from '@fortawesome/pro-light-svg-icons';
import styled from 'styled-components';
import { rem } from 'polished';
import { Text } from '../../../components/Text';

type StyledTableOfContentProps = {
  container: boolean;
};

export const StyledTableOfContent = styled.div<StyledTableOfContentProps>`
  ${({ theme, container }) =>
    container &&
    theme.breakpoints.tablet`
      position: sticky;
      top: 0;
      border-radius: ${rem(`8px`)};
      background-color: ${theme.colors.blue_1_65};
      padding: ${rem(`16px`)};
  `}
  .toc-header {
    margin-bottom: ${rem(`12px`)};
  }
  .article-anchors {
    max-width: ${rem(`315px`)};
    position: sticky;
    top: 75px;
  }
  .anchors-list__item {
    margin-bottom: ${rem(`6px`)};
  }
  .anchor-label-container {
    position: relative;
  }
  .link {
    margin-bottom: ${rem(`12px`)};
    &__icon {
      margin-left: ${rem(`6px`)};
    }
    &:hover {
      .anchor-title {
        transition: color 200ms ease-in-out;
        color: ${({ theme }) => theme.colors.pink_1};
      }
    }
  }
  .anchor-icon {
    color: ${({ theme }) => theme.colors.pink_1};
    position: absolute;
    left: 0;
    top: ${rem(`4px`)};
  }

  .anchor-title {
    transition: color 200ms ease-in-out;
    margin-left: ${rem(`6px`)};
    color: ${({ theme }) => theme.colors.gray_3};
  }

  .dim-anchor {
    color: ${({ theme }) => theme.colors.gray_4};
    transition: 200ms color ease-in-out;
  }

  .dim-icon {
    color: ${({ theme }) => theme.colors.gray_4};
    transition: 200ms color ease-in-out;
    &--disable {
      color: ${({ theme }) => theme.colors.yellow_1};
    }
  }

  .mark-anchor {
    color: ${({ theme }) => theme.colors.yellow_1};
    transition: 200ms color ease-in-out;
  }

  .highlight-anchor {
    color: ${({ theme }) => theme.colors.pink_1};
    transition: 200ms color ease-in-out;
  }

  .highlight-icon {
    color: ${({ theme }) => theme.colors.yellow_1};
    transition: 200ms color ease-in-out;
  }

  .level-1 {
    margin-left: 0;
    padding-left: ${rem(`20px`)};
  }

  .level-2 {
    margin-left: ${rem(`24px`)};
    padding-left: ${rem(`16px`)};
  }

  .level-3 {
    margin-left: ${rem(`48px`)};
    padding-left: ${rem(`10px`)};
  }
`;

interface ITableOfContent {
  className?: string;
  rawContent?: any;
  slug: string;
  headerPosition: any;
  container?: boolean;
}

export const TableOfContent = ({
  className,
  rawContent,
  slug,
  headerPosition,
  container = false,
}: ITableOfContent) => {
  const myRe = /(?<!#)^#{1,3}[\s](.+?)(?:\\n+|\n+)/gm;
  const myResult = rawContent.matchAll(myRe);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const myArray = Array.from(myResult, ([mdText, value]) => {
    const headerRegex = /(?<!#)^#{1,3}/gm;
    const headerResult = mdText.match(headerRegex)[0];
    return {
      level: headerResult.length,
      key: slugs.slug(value, false),
      label: value,
    };
  });
  let currentParentIndex = 0;
  let currentChildIndex = 0;
  let tmpParentIndex = 0;
  let tmpChildIndex = 0;
  myArray.forEach(({ key, level }, vidx) => {
    // eslint-disable-next-line default-case
    switch (level) {
      case 1: {
        tmpParentIndex = vidx;
        tmpChildIndex = vidx;
        break;
      }
      case 2: {
        tmpChildIndex = vidx;
        break;
      }
    }
    if (key === headerPosition) {
      currentParentIndex = tmpParentIndex;
      currentChildIndex = tmpChildIndex;
    }
  });
  const currentIndex = myArray.findIndex(({ key }) => key === headerPosition);
  const iconByLevel = {
    1: faHorizontalRule,
    2: faDash,
    3: faHyphen,
  };

  return (
    <StyledTableOfContent className={className} container={container}>
      <Text noRhythem type="title4" className="toc-header">
        Table of content
      </Text>
      <div className="article-anchors">
        {myArray.map(({ key, label, level }, idx) => (
          <AnchorLink
            key={key}
            to={`/blog/${slug}#${key}`}
            title={label}
            stripHash
            className="anchors-list__item"
          >
            <Text
              type="body3"
              tag="div"
              color="gray_3"
              className="link"
              noRhythem
            >
              <div className={`anchor-label-container level-${level}`}>
                <FontAwesomeIcon
                  className={`anchor-icon ${
                    headerPosition === key ? `highlight-icon` : ``
                  } ${idx < currentIndex ? `dim-icon` : ``} ${
                    idx >= currentParentIndex && idx < currentIndex
                      ? `dim-icon--disable`
                      : ``
                  }`}
                  size="1x"
                  icon={iconByLevel[level]}
                />

                <span
                  className={`anchor-title  ${
                    idx < currentIndex ? `dim-anchor` : ``
                  } ${
                    idx === currentParentIndex || idx === currentChildIndex
                      ? `mark-anchor`
                      : ``
                  } ${headerPosition === key ? `highlight-anchor` : ``}`}
                >
                  {label}
                </span>
              </div>
            </Text>
          </AnchorLink>
        ))}
      </div>
    </StyledTableOfContent>
  );
};
